import { CardType, PaymentRail } from '../../../payment.types';
import type { JsonObject } from '../../../common.types';

export const CardPointeTokenizationResultType = {
    CARDSECURE_BROWSER_TOKENIZATION: 'cardpointe_cardsecure_browser_tokenization_v1',
    CARDSECURE_BROWSER_TOKENIZATION_V2: 'cardpointe_cardsecure_browser_tokenization_v2',
    CARDSECURE_HOSTED_IFRAME_TOKENIZATION: 'cardpointe_cardsecure_hosted_iframe_tokenization_v1',
    CARDSECURE_HOSTED_IFRAME_TOKENIZATION_V2: 'cardpointe_cardsecure_hosted_iframe_tokenization_v2',
    STORED_CARD_METADATA: 'cardpointe_stored_card_metadata_v1',
} as const;

export type CardPointeTokenizationResultType =
    (typeof CardPointeTokenizationResultType)[keyof typeof CardPointeTokenizationResultType];

export const CardPointeBrowserTokenizationSessionType =
    'cardpointe_cardsecure_browser_tokenization_session_v1' as const;

export interface CardPointeCardSecureBrowserTokenizationSession {
    readonly browser_tokenization_session_type: typeof CardPointeBrowserTokenizationSessionType;
    readonly card_secure_base_url: string;
}

export interface CardPointeCardSecureTokenizationSuccessResponse extends JsonObject {
    readonly message: 'No error';
    readonly errorcode: 0;
    readonly token: string;
    readonly expiry?: string;
}

export interface CardPointeHostedIframeEnhancedSuccessResponse extends JsonObject {
    readonly token: string;
    readonly expiry: string;
    readonly errorCode: '0' | 0;
    readonly errorMessage?: string;
}

export interface CardPointeHostedIframeBasicSuccessResponse extends JsonObject {
    readonly message: string;
    readonly expiry: string;
}

export type CardPointeHostedIframeSuccessResponse =
    | CardPointeHostedIframeEnhancedSuccessResponse
    | CardPointeHostedIframeBasicSuccessResponse;

export interface CardPointeCreditCardTokenizedCardMetadata {
    readonly payment_rail: typeof PaymentRail.CREDIT_CARD;
    readonly masked_pan: string;
    readonly card_type: typeof CardType.CREDIT | typeof CardType.UNKNOWN;
    readonly card_brand: string;
    readonly cardholder_name: string;
    readonly expiry_date?: string;
}

export interface CardPointeDebitCardTokenizedCardMetadata {
    readonly payment_rail: typeof PaymentRail.DEBIT_CARD;
    readonly masked_pan: string;
    readonly card_type: typeof CardType.DEBIT;
    readonly card_brand: string;
    readonly cardholder_name: string;
    readonly expiry_date?: string;
}

export type CardPointeTokenizedCardMetadata =
    | CardPointeCreditCardTokenizedCardMetadata
    | CardPointeDebitCardTokenizedCardMetadata;

export interface CardPointeCardSecureBrowserTokenizationResult {
    readonly result_type: typeof CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION;
    readonly cardsecure_response: CardPointeCardSecureTokenizationSuccessResponse;
    readonly card_metadata?: CardPointeTokenizedCardMetadata;
}

export interface CardPointeCardSecureBrowserTokenizationResultV2 {
    readonly result_type: typeof CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION_V2;
    readonly provider_response: CardPointeCardSecureTokenizationSuccessResponse;
    readonly token: string;
    readonly card_metadata?: CardPointeTokenizedCardMetadata;
}

export interface CardPointeCardSecureHostedIframeTokenizationResult {
    readonly result_type: typeof CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION;
    readonly cardsecure_response: CardPointeHostedIframeSuccessResponse;
    readonly card_metadata?: CardPointeTokenizedCardMetadata;
}

export interface CardPointeCardSecureHostedIframeTokenizationResultV2 {
    readonly result_type: typeof CardPointeTokenizationResultType.CARDSECURE_HOSTED_IFRAME_TOKENIZATION_V2;
    readonly provider_response: CardPointeHostedIframeSuccessResponse;
    readonly token: string;
    readonly card_metadata?: CardPointeTokenizedCardMetadata;
}

export interface CardPointeStoredCardMetadataTokenizationResult {
    readonly result_type: typeof CardPointeTokenizationResultType.STORED_CARD_METADATA;
    readonly token: string;
    readonly card_metadata: CardPointeTokenizedCardMetadata;
}

export type CardPointeTokenizationResult =
    | CardPointeCardSecureBrowserTokenizationResult
    | CardPointeCardSecureBrowserTokenizationResultV2
    | CardPointeCardSecureHostedIframeTokenizationResult
    | CardPointeCardSecureHostedIframeTokenizationResultV2
    | CardPointeStoredCardMetadataTokenizationResult;
